import type { LanguageCode, TranscriptMeta, VoiceSessionReport } from '@ampshift/shared';
import type { ApiClient } from '../api/client';
/** Quiet period after a final Transcribe segment before we treat the command as complete. */
const FINAL_SEGMENT_GRACE_MS = 700;
import { createVadState, DEFAULT_VAD_CONFIG, frameDurationMs, frameRms, processFrame, type VadConfig, type VadState } from './vad';
import { PCM_SAMPLE_RATE, startPcmCapture, stopStreamTracks, type PcmCapture, type PcmChunkHandler } from './audio-worklet';
import { TranscribeStreamClient } from './transcribe-client';

export type VoiceStatus = 'idle' | 'requestingMic' | 'connecting' | 'listening' | 'finalizing' | 'error';
export type ListeningMode = 'PRESS_TO_TOGGLE' | 'PRESS_AND_HOLD';

export interface VoiceSnapshot {
  status: VoiceStatus;
  /** Normalised RMS level 0..1 of the last frame. */
  level: number;
  elapsedMs: number;
  /** Live transcript ("hearing…"): finals + partial. */
  partial: string;
  language?: string;
  error?: string;
  errorKind?: 'mic-denied' | 'mic-unavailable' | 'insecure' | 'network' | 'stream';
  hasSpeech: boolean;
  mode: ListeningMode;
}

export interface VoiceConfig {
  /** Explicit language, or undefined for automatic identification among allowed languages. */
  language?: LanguageCode;
  autoLanguageIdentification: boolean;
  silenceThresholdMs: number;
  maxCommandSeconds: number;
  mode: ListeningMode;
}

export interface VoiceCommand {
  text: string;
  meta: TranscriptMeta;
}

export interface VoiceControllerDeps {
  api: Pick<ApiClient, 'transcribeSession' | 'sessionReport'>;
  getConfig: () => VoiceConfig;
  /** Receives the finalised transcript; the caller posts it to /v1/chat with inputMode 'voice'. */
  onCommand: (command: VoiceCommand) => void;
  getUserMedia?: (constraints: MediaStreamConstraints) => Promise<MediaStream>;
  createCapture?: (stream: MediaStream, onChunk: PcmChunkHandler) => Promise<PcmCapture>;
  /** How long a listening session may deliver no audio frames before it is failed (default 2500 ms). */
  noAudioTimeoutMs?: number;
  WebSocketCtor?: typeof WebSocket;
  now?: () => number;
  isSecureContext?: () => boolean;
}

type StopReason = 'COMMAND' | 'TIMEOUT';

const IDLE: VoiceSnapshot = { status: 'idle', level: 0, elapsedMs: 0, partial: '', hasSpeech: false, mode: 'PRESS_TO_TOGGLE' };

/**
 * State machine: idle → requestingMic → connecting → listening → finalizing → idle | error.
 * Audio is never buffered: each PCM chunk goes straight from the capture node to the WebSocket and is dropped.
 */
export class VoiceController {
  private snapshot: VoiceSnapshot;
  private readonly listeners = new Set<() => void>();
  private stream: MediaStream | undefined;
  private capture: PcmCapture | undefined;
  private client: TranscribeStreamClient | undefined;
  private vad: VadState = createVadState();
  private finalTimer: ReturnType<typeof setTimeout> | undefined;
  private noAudioTimer: ReturnType<typeof setTimeout> | undefined;
  private chunksSeen = 0;
  private lastPartialText = '';
  private finalizedText = '';
  private vadConfig: VadConfig = DEFAULT_VAD_CONFIG;
  private startedAt = 0;
  private maxTimer: ReturnType<typeof setTimeout> | undefined;
  private sessionId: string | undefined;
  private generation = 0;
  private holdReleasedEarly = false;
  private disposed = false;
  private readonly now: () => number;

  constructor(private readonly deps: VoiceControllerDeps) {
    this.now = deps.now ?? (() => Date.now());
    this.snapshot = { ...IDLE, mode: deps.getConfig().mode };
  }

  // ------------------------------------------------------------ store API
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  getSnapshot = (): VoiceSnapshot => this.snapshot;

  private update(patch: Partial<VoiceSnapshot>): void {
    this.snapshot = { ...this.snapshot, ...patch };
    for (const l of Array.from(this.listeners)) l();
  }

  get status(): VoiceStatus {
    return this.snapshot.status;
  }

  get isActive(): boolean {
    const s = this.snapshot.status;
    return s === 'requestingMic' || s === 'connecting' || s === 'listening' || s === 'finalizing';
  }

  /** Re-reads the listening mode from config (after a settings change). */
  refreshMode(): void {
    const mode = this.deps.getConfig().mode;
    if (mode !== this.snapshot.mode) this.update({ mode });
  }

  // ------------------------------------------------------------ user intents
  /** Press-to-toggle: start when idle, otherwise stop/cancel. */
  toggle(): void {
    const s = this.snapshot.status;
    if (s === 'idle' || s === 'error') void this.start();
    else if (s === 'listening') void this.stop('COMMAND');
    else if (s === 'requestingMic' || s === 'connecting') this.cancel('CANCELLED');
  }

  /** Press-and-hold: key/pointer down. */
  pressStart(): void {
    const s = this.snapshot.status;
    this.holdReleasedEarly = false;
    if (s === 'idle' || s === 'error') void this.start();
  }

  /** Press-and-hold: key/pointer up — finalises the command. */
  pressEnd(): void {
    const s = this.snapshot.status;
    if (s === 'listening') void this.stop('COMMAND');
    else if (s === 'requestingMic' || s === 'connecting') this.holdReleasedEarly = true;
  }

  /** Esc, sign-out, pagehide, session expiry: stop everything, discard the transcript. */
  cancel(outcome: VoiceSessionReport['outcome'] = 'CANCELLED'): void {
    if (!this.isActive) return;
    const gen = ++this.generation;
    const client = this.client;
    const sessionId = this.sessionId;
    this.teardownAudio();
    const result = client?.result();
    client?.abort();
    this.client = undefined;
    this.sessionId = undefined;
    this.update({ ...IDLE, mode: this.snapshot.mode });
    if (sessionId && client) {
      void this.report({ voiceSessionId: sessionId, streamDurationMs: result?.streamDurationMs ?? 0, outcome, finalTranscriptLength: result?.transcript.length ?? 0 }, gen);
    }
  }

  dispose(): void {
    this.disposed = true;
    this.cancel('CANCELLED');
    this.listeners.clear();
  }

  // ------------------------------------------------------------ lifecycle
  async start(): Promise<void> {
    if (this.disposed || this.isActive) return;
    const gen = ++this.generation;
    const config = this.deps.getConfig();
    this.vadConfig = { ...DEFAULT_VAD_CONFIG, silenceThresholdMs: config.silenceThresholdMs, maxCommandSeconds: config.maxCommandSeconds };
    this.vad = createVadState();
    this.update({ ...IDLE, mode: config.mode, status: 'requestingMic' });

    if (this.deps.isSecureContext ? !this.deps.isSecureContext() : typeof window !== 'undefined' && window.isSecureContext === false) {
      this.fail('Voice input requires a secure context (https or localhost).', 'insecure');
      return;
    }

    // 1. Microphone
    let stream: MediaStream;
    try {
      const gum = this.deps.getUserMedia ?? ((c: MediaStreamConstraints) => navigator.mediaDevices.getUserMedia(c));
      stream = await gum({ audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
    } catch (err) {
      const name = err instanceof DOMException || (err instanceof Error && 'name' in err) ? (err as Error).name : '';
      if (name === 'NotAllowedError' || name === 'SecurityError' || name === 'PermissionDeniedError') this.fail('Microphone access was denied.', 'mic-denied');
      else if (name === 'NotFoundError' || name === 'OverconstrainedError') this.fail('No microphone is available.', 'mic-unavailable');
      else this.fail(`Microphone error: ${err instanceof Error ? err.message : String(err)}`, 'mic-unavailable');
      return;
    }
    if (gen !== this.generation) {
      stopStreamTracks(stream);
      return;
    }
    this.stream = stream;
    this.update({ status: 'connecting' });

    // 2. Presigned stream + WebSocket
    let client: TranscribeStreamClient;
    try {
      const session = await this.deps.api.transcribeSession({
        ...(config.language ? { language: config.language } : {}),
        autoLanguageIdentification: config.autoLanguageIdentification || !config.language,
        sampleRateHz: 16000,
      });
      if (gen !== this.generation) {
        stopStreamTracks(stream);
        return;
      }
      this.sessionId = session.voiceSessionId;
      if (typeof session.maxDurationSeconds === 'number' && session.maxDurationSeconds > 0) {
        this.vadConfig = { ...this.vadConfig, maxCommandSeconds: Math.min(this.vadConfig.maxCommandSeconds, session.maxDurationSeconds) };
      }
      client = new TranscribeStreamClient(
        session,
        {
          onPartial: (text) => {
            if (gen !== this.generation) return;
            // The client emits a partial right after every final (the joined finalized text). Only text that
            // grows beyond the last finalized transcript means the user is still talking.
            if (text.length > this.finalizedText.length && text !== this.lastPartialText) this.clearFinalTimer();
            this.lastPartialText = text;
            this.update({ partial: text });
          },
          onFinal: (segment) => {
            if (gen !== this.generation || this.snapshot.status !== 'listening') return;
            this.finalizedText = this.finalizedText ? `${this.finalizedText} ${segment}` : segment;
            // Server-side endpointing fallback: Transcribe emits a final segment once it has heard silence.
            // If nothing new arrives shortly after, end the command even when the local VAD missed the speech.
            this.clearFinalTimer();
            this.finalTimer = setTimeout(() => {
              if (gen === this.generation && this.snapshot.status === 'listening') void this.stop('COMMAND');
            }, FINAL_SEGMENT_GRACE_MS);
          },
          onError: (err) => {
            if (gen === this.generation && this.isActive) this.failStream(err);
          },
        },
        { ...(this.deps.WebSocketCtor ? { WebSocketCtor: this.deps.WebSocketCtor } : {}), now: this.now },
      );
      await client.connect();
    } catch (err) {
      if (gen === this.generation) {
        stopStreamTracks(stream);
        this.stream = undefined;
        this.fail(err instanceof Error ? err.message : String(err), 'network');
      } else stopStreamTracks(stream);
      return;
    }
    if (gen !== this.generation) {
      client.abort();
      stopStreamTracks(stream);
      return;
    }
    this.client = client;

    // 3. Capture graph → WebSocket, VAD in the same hop
    try {
      const create = this.deps.createCapture ?? startPcmCapture;
      this.capture = await create(stream, (pcm) => this.onChunk(gen, pcm));
    } catch (err) {
      if (gen === this.generation) {
        client.abort();
        stopStreamTracks(stream);
        this.stream = undefined;
        this.client = undefined;
        this.fail(`Audio capture failed: ${err instanceof Error ? err.message : String(err)}`, 'mic-unavailable');
      }
      return;
    }
    if (gen !== this.generation) {
      this.teardownAudio();
      client.abort();
      return;
    }
    this.startedAt = this.now();
    this.chunksSeen = 0;
    this.update({ status: 'listening', elapsedMs: 0, level: 0, ...(config.language ? { language: config.language } : {}) });
    // Watchdog: a capture graph that delivers no frames (suspended AudioContext, muted track) must not sit
    // silently "listening"; surface it so the user can retry instead of wondering why nothing is heard.
    this.noAudioTimer = setTimeout(() => {
      if (gen === this.generation && this.snapshot.status === 'listening' && this.chunksSeen === 0) {
        this.cancel('ERROR');
        this.fail('No audio is reaching the app. Check the microphone permission, close other apps using the microphone, then try again.', 'mic-unavailable');
      }
    }, this.deps.noAudioTimeoutMs ?? 2500);
    this.maxTimer = setTimeout(() => {
      if (gen === this.generation && this.snapshot.status === 'listening') void this.stop('TIMEOUT');
    }, this.vadConfig.maxCommandSeconds * 1000 + 500);

    if (config.mode === 'PRESS_AND_HOLD' && this.holdReleasedEarly) {
      this.holdReleasedEarly = false;
      void this.stop('COMMAND');
    }
  }

  private onChunk(gen: number, pcm: Int16Array): void {
    if (gen !== this.generation || this.snapshot.status !== 'listening' || !this.client) return;
    this.chunksSeen += 1;
    const frameMs = frameDurationMs(pcm.length, PCM_SAMPLE_RATE);
    const rms = frameRms(pcm);
    const { state, event } = processFrame(this.vad, this.vadConfig, rms, frameMs);
    this.vad = state;
    this.client.sendAudio(pcm);
    this.update({ level: rms, elapsedMs: this.now() - this.startedAt, hasSpeech: state.hasSpeech });
    if (event === 'end-of-command') void this.stop('COMMAND');
    else if (event === 'max-duration') void this.stop('TIMEOUT');
  }

  /** Finalises: stop capture FIRST (no more audio leaves the browser), drain the stream, report, dispatch. */
  async stop(reason: StopReason): Promise<void> {
    if (this.snapshot.status !== 'listening' || !this.client) return;
    const gen = this.generation;
    const client = this.client;
    const sessionId = this.sessionId ?? '';
    this.update({ status: 'finalizing', level: 0 });
    this.teardownAudio();
    let result;
    try {
      result = await client.end();
    } catch (err) {
      result = client.result();
      this.failStream(err instanceof Error ? err : new Error(String(err)));
      return;
    }
    if (gen !== this.generation) return;
    this.client = undefined;
    this.sessionId = undefined;
    const transcript = result.transcript;
    const outcome: VoiceSessionReport['outcome'] = transcript.length === 0 ? 'EMPTY' : reason === 'TIMEOUT' ? 'TIMEOUT' : 'COMMAND';
    await this.report({ voiceSessionId: sessionId, streamDurationMs: result.streamDurationMs, outcome, finalTranscriptLength: transcript.length }, gen);
    const language = (result.languageCode ?? this.snapshot.language) as LanguageCode | undefined;
    this.update({ ...IDLE, mode: this.snapshot.mode, partial: '', ...(result.languageCode ? { language: result.languageCode } : {}) });
    if (transcript.length > 0) {
      const meta: TranscriptMeta = { streamDurationMs: result.streamDurationMs, voiceSessionId: sessionId };
      if (language && (language === 'en-US' || language === 'ar-AE' || language === 'ar-SA')) meta.language = language;
      if (result.identifiedLanguage) meta.identifiedLanguage = result.identifiedLanguage;
      if (typeof result.confidence === 'number') meta.confidence = result.confidence;
      this.deps.onCommand({ text: transcript, meta });
    }
  }

  private async report(report: VoiceSessionReport, gen: number): Promise<void> {
    if (!report.voiceSessionId) return;
    try {
      await this.deps.api.sessionReport(report);
    } catch {
      /* metering is best-effort from the client; the API also tracks the presign */
    }
    void gen;
  }

  private clearFinalTimer(): void {
    if (this.finalTimer) {
      clearTimeout(this.finalTimer);
      this.finalTimer = undefined;
    }
  }

  private teardownAudio(): void {
    this.clearFinalTimer();
    if (this.noAudioTimer) {
      clearTimeout(this.noAudioTimer);
      this.noAudioTimer = undefined;
    }
    this.lastPartialText = '';
    this.finalizedText = '';
    if (this.maxTimer) {
      clearTimeout(this.maxTimer);
      this.maxTimer = undefined;
    }
    const capture = this.capture;
    this.capture = undefined;
    if (capture) void capture.stop();
    stopStreamTracks(this.stream);
    this.stream = undefined;
  }

  private fail(message: string, kind: VoiceSnapshot['errorKind']): void {
    this.teardownAudio();
    this.update({ ...IDLE, mode: this.snapshot.mode, status: 'error', error: message, ...(kind ? { errorKind: kind } : {}) });
  }

  private failStream(err: Error): void {
    const gen = ++this.generation;
    const client = this.client;
    const sessionId = this.sessionId;
    this.teardownAudio();
    const result = client?.result();
    client?.abort();
    this.client = undefined;
    this.sessionId = undefined;
    this.update({ ...IDLE, mode: this.snapshot.mode, status: 'error', error: err.message, errorKind: 'stream' });
    if (sessionId) void this.report({ voiceSessionId: sessionId, streamDurationMs: result?.streamDurationMs ?? 0, outcome: 'ERROR', finalTranscriptLength: result?.transcript.length ?? 0 }, gen);
  }

  /** Clears an error state back to idle. */
  clearError(): void {
    if (this.snapshot.status === 'error') this.update({ ...IDLE, mode: this.snapshot.mode });
  }
}

/**
 * Wires the mandatory shutdown paths: window `pagehide`/`beforeunload`, window blur during press-and-hold,
 * and an external sign-out/session-expiry signal. Returns an unbind function.
 */
export function bindVoiceLifecycle(controller: VoiceController, opts: { onSignOut: (cb: () => void) => () => void; target?: Window }): () => void {
  const win = opts.target ?? window;
  const cancel = (): void => controller.cancel('CANCELLED');
  const blur = (): void => {
    if (controller.getSnapshot().mode === 'PRESS_AND_HOLD') controller.pressEnd();
  };
  win.addEventListener('pagehide', cancel);
  win.addEventListener('beforeunload', cancel);
  win.addEventListener('blur', blur);
  const unsub = opts.onSignOut(cancel);
  return () => {
    win.removeEventListener('pagehide', cancel);
    win.removeEventListener('beforeunload', cancel);
    win.removeEventListener('blur', blur);
    unsub();
  };
}
